import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { Check, Loader2, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@bookdental/ui";
import { cn } from "@/lib/cn";

export type EstagioDestino = {
  /** `null` = "Sem estágio". */
  id: string | null;
  rotulo: string;
  cor?: string | null;
};

type Props = {
  aberto: boolean;
  /** Título da folha — "Mover para…" no quadro; a Planilha usa "Alterar estágio" etc. */
  titulo?: string;
  /** Nome de quem está sendo movido, só pro subtítulo. */
  paciente?: string;
  estagios: EstagioDestino[];
  /** Omitido (lote com valores mistos): nenhuma opção aparece marcada como atual. */
  atualId?: string | null;
  movendo: boolean;
  onFechar: () => void;
  onEscolher: (destinoId: string | null) => void;
  /** Atalho no fim da lista pra criar um destino novo (só estágio, e só com plano que permite). */
  onCriarNovo?: () => void;
};

/**
 * Escolher um destino numa lista, no celular — estágio, modelo ou responsável.
 *
 * Substitui o arraste — que no celular exigiria soltar o card numa coluna fora da tela — e NÃO
 * cria caminho novo: quem chama passa a mesma mutation que o `onDragEnd` do quadro já usa, com as
 * mesmas validações, permissões e invalidações. Aqui só se escolhe o destino.
 */
export function MobileMoveToStageSheet({
  aberto,
  titulo = "Mover para…",
  paciente,
  estagios,
  atualId,
  movendo,
  onFechar,
  onEscolher,
  onCriarNovo,
}: Props) {
  return (
    <Sheet open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <SheetContent
        side="bottom"
        className="flex max-h-[85dvh] flex-col gap-0 rounded-t-2xl border-zinc-200 bg-white p-0 dark:border-border dark:bg-card"
      >
        <SheetHeader className="border-b border-zinc-100 px-4 py-3 text-left dark:border-border">
          <SheetTitle className="text-sm font-bold text-zinc-900 dark:text-foreground" ajuda={AJUDA_PRODUCAO.moverCelular}>
            {titulo}
          </SheetTitle>
          {paciente && (
            <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">{paciente}</p>
          )}
        </SheetHeader>

        <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
          {estagios.map((e) => {
            const atual = e.id === atualId;
            return (
              <li key={e.id ?? "__sem_estagio__"}>
                <button
                  type="button"
                  disabled={movendo || atual}
                  onClick={() => onEscolher(e.id)}
                  className={cn(
                    "flex h-12 w-full items-center gap-3 rounded-md px-3 text-left text-sm transition-colors",
                    atual
                      ? "cursor-default bg-zinc-50 font-semibold text-zinc-900 dark:bg-muted/40 dark:text-foreground"
                      : "text-zinc-800 hover:bg-zinc-50 disabled:opacity-50 dark:text-foreground dark:hover:bg-muted",
                  )}
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: e.cor ?? "#d4d4d8" }}
                  />
                  <span className="min-w-0 flex-1 truncate">{e.rotulo}</span>
                  {atual && (
                    <span className="flex shrink-0 items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                      <Check className="h-3.5 w-3.5" />
                      Atual
                    </span>
                  )}
                </button>
              </li>
            );
          })}
          {onCriarNovo && (
            <li>
              <button
                type="button"
                disabled={movendo}
                onClick={onCriarNovo}
                className="mt-1 flex h-12 w-full items-center gap-3 rounded-md border-t border-zinc-100 px-3 text-left text-sm font-semibold text-brand-700 transition-colors hover:bg-zinc-50 disabled:opacity-50 dark:border-border dark:text-primary dark:hover:bg-muted"
              >
                <Plus className="h-4 w-4 shrink-0" />
                Novo estágio
              </button>
            </li>
          )}
        </ul>

        {movendo && (
          <p className="flex items-center justify-center gap-2 border-t border-zinc-100 py-3 text-xs text-zinc-500 dark:border-border dark:text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Movendo…
          </p>
        )}
      </SheetContent>
    </Sheet>
  );
}
